<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, onMounted, getCurrentInstance, watch, useTemplateRef, onBeforeUnmount } from 'vue'
import _ from 'lodash'
import moment from 'moment'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import DestinationService from "@/services/DestinationService"
import { formatPidsSummary, isCustomSrtDestination } from '@/Utils/srtPushTarget'
import IconBase from '@/components/icon/IconBase.vue'
import IconBucket from '@/components/icon/IconBucket.vue'
import IconLink from '@/components/icon/IconLink.vue'
import IconSettings from '@/components/icon/IconSettings.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import EditDestinationModal from '@/components/modals/EditDestinationModal.vue'
import Title from '@/components/ui/Title.vue'
import FormButton from '../Atoms/FormButton.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

// Refs
const selectedDestination = ref(null)
const allowEditTitle = ref(false)

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const destinations = computed(() => store.getters['Destination/destinations'])
const platforms = computed(() => store.getters['Destination/platforms'])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])

// Method
const platformIcon = (name) => platforms.value.find(platform => platform?.name === name)?.icon ?? ''

const deleteDestination = (item) => {
	selectDestination(item)
	showModal('delete-destination-modal')
}

const editDestination = (item) => {
	selectDestination(item)
	showModal('edit-destination-modal')
}

const clearSelected = () => {
	selectedDestination.value = null
}

const selectDestination = (item) => {
	selectedDestination.value = item
}

const confirmDelete = async () => {
	await DestinationService.deleteDestination(selectedDestination.value._id)
	store.dispatch('Destination/getDestinations', true) // force refresh after delete
}

const saveNewDestinationName = async (newName) => {
	const id = selectedDestination.value._id
	const name = newName
	await DestinationService.updateDestination(id, name)
	notify({ group: 'success', text: 'The destination was renamed successfully.' })
	store.dispatch('Destination/getDestinations', true) // force refresh after update
}

const showLinkedStreams = (streams) => {
	return streams.length === 1
		? allInOneStreams.value.find(stream => stream._id === streams[0]._id)?.name
		: `Connected to ${streams.length} streams`
}

</script>

<template>
	<div>
		<ul class="mt-6 flex flex-col gap-3">
			<li v-for="item in destinations" :key="item._id"
				class="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-surface-2 rounded-xl p-3 sm:p-4 ring-1 ring-white/[0.04] hover:ring-white/10 transition-colors">
				<div class="shrink-0 w-16 h-11 rounded-lg bg-surface-1 flex items-center justify-center overflow-hidden">
					<img v-if="platformIcon(item.template)" class="max-w-[52px] max-h-8 object-contain"
						:src="platformIcon(item.template)" :alt="item.name" />
					<icon-base v-else width-view-box="24" height-view-box="24" iconColor="none" width="22" height="22"
						class="text-surface-7">
						<icon-settings />
					</icon-base>
				</div>
				<div class="min-w-0 grow">
					<Title :editable="true"
						titleClasses="inline-flex items-center h-8 -ml-2 px-2 max-w-full rounded-md text-base font-medium hover:bg-surface-3"
						:title="item.name" @edit="selectDestination(item)" @changed="saveNewDestinationName" />
					<p class="m-0 text-tiny text-surface-7 truncate">{{ item.server }}</p>
					<p v-if="isCustomSrtDestination(item) && formatPidsSummary(item.pids, item.pmtPid)"
						class="m-0 text-tiny text-surface-7 truncate" data-testid="destination-pids">
						PIDs: {{ formatPidsSummary(item.pids, item.pmtPid) }}
					</p>
					<p class="m-0 mt-0.5 text-tiny truncate flex items-center"
						:class="item.linkedStreams && item.linkedStreams.length > 0 ? 'text-surface-8' : 'text-surface-6'">
						<icon-base width-view-box="14" height-view-box="14" iconColor="none" width="14" height="14"
							class="mr-1.5 shrink-0">
							<icon-link />
						</icon-base>
						{{ (item.linkedStreams && item.linkedStreams.length > 0)
							? showLinkedStreams(item.linkedStreams)
							: 'Not connected to any stream' }}
					</p>
				</div>
				<div class="shrink-0 flex items-center gap-2 self-end sm:self-center">
					<FormButton @click="editDestination(item)" size="sm" type="secondary">
						Edit destination
					</FormButton>
					<FormButton @click="deleteDestination(item)" size="sm" type="secondary" is-icon aria-label="Delete destination"
						class="!text-surface-7 hover:!text-danger">
						<icon-base width-view-box="20" height-view-box="21" iconColor="none" width="20" height="20" classIcon="">
							<icon-bucket />
						</icon-base>
					</FormButton>
				</div>
			</li>
		</ul>
		<confirm-modal modal-id="delete-destination-modal"
			:message='`Do you really want to delete the ${selectedDestination && selectedDestination.name} destination?`'
			cancel-text="Cancel" modal-type="danger" @modal-hidden="clearSelected" ok-text="Delete"
			@modal-confirm="confirmDelete()" />
		<EditDestinationModal :destination="selectedDestination" />
	</div>
</template>

<style scoped></style>